import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-lg border border-border/60 dark:border-border transition-shadow duration-150",
  {
    variants: {
      variant: {
        default: "hover:shadow-md shadow-sm bg-card/50 dark:bg-card/80 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/5",
        interactive: "cursor-pointer transition-all duration-150 hover:shadow-lg shadow-sm bg-card/50 dark:bg-card/80 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/5",
        player: "rounded-3xl border-2 border-slate-200/60 dark:border-slate-700/60 shadow-[0_8px_30px_rgba(99,102,241,0.12),0_20px_50px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_30px_rgba(59,130,246,0.1),0_20px_50px_rgba(0,0,0,0.4)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.12),0_30px_70px_rgba(0,0,0,0.15),0_0_40px_rgba(99,102,241,0.05)] dark:hover:shadow-[0_12px_40px_rgba(59,130,246,0.12),0_30px_70px_rgba(0,0,0,0.5),0_0_50px_rgba(59,130,246,0.08)] hover:border-blue-300/50 dark:hover:border-blue-500/35 [@media(hover:hover)_and_(pointer:fine)]:hover:-translate-y-1 bg-gradient-to-br from-white from-30% via-blue-50/80 via-70% to-indigo-100/60 dark:from-slate-800 dark:via-slate-800/95 dark:to-slate-900/90 ring-1 ring-slate-200/50 dark:ring-slate-600/30 hover:ring-blue-200/60 dark:hover:ring-blue-500/20 transition-all duration-300 relative overflow-hidden before:absolute before:inset-0 before:rounded-3xl before:p-[2px] before:bg-gradient-to-br before:from-blue-400/10 before:via-transparent before:to-indigo-400/10 dark:before:from-blue-400/15 dark:before:via-transparent dark:before:to-indigo-500/15 before:pointer-events-none before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300 after:absolute after:inset-0 after:bg-[linear-gradient(110deg,transparent_30%,rgba(99,102,241,0.03)_45%,rgba(99,102,241,0.06)_50%,rgba(99,102,241,0.03)_55%,transparent_70%)] dark:after:bg-[linear-gradient(110deg,transparent_30%,rgba(150,180,255,0.04)_45%,rgba(150,180,255,0.08)_50%,rgba(150,180,255,0.04)_55%,transparent_65%)] after:bg-[length:250%_100%] after:pointer-events-none after:translate-x-[-100%] hover:after:animate-[shimmer_0.7s_ease-out_forwards]",
        stats: "shadow-md hover:shadow-lg transition-shadow bg-card/50 dark:bg-card/80 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/5",
        elevated: "shadow-lg bg-card/80 dark:bg-card/90 backdrop-blur-sm border-border/80 dark:border-border ring-1 ring-black/5 dark:ring-white/5",
      },
      size: {
        default: "",
        compact: "p-4",
        comfortable: "p-6",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof cardVariants> {
  interactive?: boolean
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        cardVariants({
          variant: variant || (interactive ? "interactive" : undefined),
          size
        }),
        className
      )}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

const InteractiveCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg border cursor-pointer transition-all duration-150 hover:shadow-lg hover:-translate-y-1 hover:scale-[1.02]",
      className
    )}
    {...props}
  />
))
InteractiveCard.displayName = "InteractiveCard"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, InteractiveCard }
